import React, { useContext, useEffect, useState } from 'react';
import { Navigate, useNavigate, useRoutes, useSearchParams } from 'react-router-dom'; 
import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import { Helmet } from 'react-helmet';

import ProductTourReducer from 'containers/ProductTour/reducer';
import ProductTourSaga from 'containers/ProductTour/saga';
import CheckoutJourenyReducer from 'containers/CheckoutJourney/reducer';
import LandingPageReducer from 'containers/LandingPage/reducer';
import LandingPageSaga from 'containers/LandingPage/saga';

import {
  useProductFruits,
  useProductFruitsReady,
  Space,
} from "@cashfree-intl/coherent";

import {
  ChoiceScreen,
  GetStartScreen,
  PluginSelection,
  PlatformSelection,
  IntegrationScreen,
  CheckoutIntegration,
  SuccessScreen,
} from "./screens";

import { MerchantContext } from "contexts/MerchantContext";
import {
  findChecklistByName,
  getHighestPriorityCampaign,
  getUnfinishedChecklists,
} from "containers/ProductTour/helper";
import { DAY_ZERO_CHECKOUT_JOURNEY, TEST } from "utils/constants";
import { CHECK_LIST_ELIGIBLE_STATUS, CHECK_LIST_STATUS } from "containers/ProductTour/constants";
import { ProductDescription, TopContainer } from "./styled";
import { basename } from '../../store';



const CheckoutJourney = () => {
  const navigate = useNavigate();
  const [checklist, setChecklistItem] = useState(null);
  const [isLoading, setIsLoading] = useState(true); 
  const [shouldRenderContent, setShouldRenderContent] = useState(false);

  const { productFruitCampaigns } = useContext(MerchantContext);

  useInjectReducer({ key: "productTour", reducer: ProductTourReducer });
  useInjectSaga({ key: "productTour", saga: ProductTourSaga, mode: "" });
  useInjectReducer({ key: 'landingPage', reducer : LandingPageReducer});
  useInjectSaga({ key: 'landingPage', saga:LandingPageSaga, mode: "" });
  
  useInjectReducer({
    key: "checkoutJourney",
    reducer: CheckoutJourenyReducer,
  });

  const { getChecklists, updateIdentity, isChecklistEligible } =
    useProductFruits();

  const highestPriority = getHighestPriorityCampaign(
    productFruitCampaigns?.campaigns,
  );

  const activeCampaignId = highestPriority?.campaignId
    ? Number(highestPriority.campaignId)
    : null;

  const isReady = useProductFruitsReady();

  // isTest
  const [searchParams] = useSearchParams();
  const qsEnv = searchParams.get("env") || null;
  const isTestEnv = qsEnv === TEST;

  useEffect(() => {
    fetchProductTourData();
  }, [isReady]);

  const fetchProductTourData = async () => {
    
    if (!isReady) {
      setIsLoading(false);
      return;
    }

    if (isTestEnv || !activeCampaignId) {
      setIsLoading(false);
      navigate(`${basename}landing`);
      return;
    }

    setIsLoading(true);

    try {
      const isEligible = await isChecklistEligible(activeCampaignId);
      
      if (isEligible !== CHECK_LIST_ELIGIBLE_STATUS.ELIGIBLE) {
        navigate(`${basename}landing`);
        return;
      } 

    
      const checklists = await getChecklists();

      const unfinishedChecklists = getUnfinishedChecklists(checklists);
      
      if (unfinishedChecklists.length === 0) {
        navigate(`${basename}landing`);
        return;
      }

      const checkoutJourneyChecklist = findChecklistByName(
        checklists,
        DAY_ZERO_CHECKOUT_JOURNEY,
      );

      if (!checkoutJourneyChecklist || checkoutJourneyChecklist.state === CHECK_LIST_STATUS.FINISHED) {
        navigate(`${basename}landing`);
        return;
      }

      await updateIdentity({ DAY_ZERO_CHECKOUT_JOURNEY: true });
      setChecklistItem(checkoutJourneyChecklist);
      setShouldRenderContent(true);
      
    } catch (error) {
      navigate(`${basename}landing`);
    } finally {
      setIsLoading(false);
    }
  };

  const routes = [
    {
      path: "",
      element: (
        <GetStartScreen checklist={checklist} />
      ),
    },
    {
      path: "choice-screen",
      element: (
        <ChoiceScreen checklist={checklist} goBack={() => {}} />
      ),
    },
    {
      path: "platform-selection",
      element: <PlatformSelection />,
    },
    {
      path: "plugin-selection",
      element: <PluginSelection />,
    },
    {
      path: "checkout-integration",
      element: <CheckoutIntegration />,
    },
    {
      path: "integration",
      element: <IntegrationScreen />,
    },
    {
      path: "success",
      element: <SuccessScreen />,
    },
    {
      path: "*",
      element: <Navigate to={`${basename}checkout-journey`} />, 
    },
  ];

  const element = useRoutes(routes);

  if (isLoading) {
    return (
      <TopContainer>
        <Space direction="column" align="center" justify="center" fullHeight>
          <ProductDescription> Loading . . .</ProductDescription>
        </Space>
      </TopContainer>
    );
  }

  if (!shouldRenderContent) {
    return null;
  }

  
  return (
      <>
      <Helmet>
        <title>Checkout Journey</title>
        <meta name="description" content="Checkout Customisation" />
      </Helmet>          
      {element}
     </>
  )
};

export default CheckoutJourney;